import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mail, AlertTriangle, CheckCircle2, RefreshCw, Inbox, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

const EVENT_LABEL = {
  'email.delivered': { label: 'Delivered', tone: 'success' },
  'email.bounced': { label: 'Bounced', tone: 'danger' },
  'email.complained': { label: 'Spam complaint', tone: 'danger' },
  'email.opened': { label: 'Opened', tone: 'neutral' },
  'email.clicked': { label: 'Clicked', tone: 'neutral' },
  'email.sent': { label: 'Sent', tone: 'neutral' },
  'email.delivery_delayed': { label: 'Delayed', tone: 'warning' },
};

function eventBadge(eventType) {
  const meta = EVENT_LABEL[eventType] || { label: eventType, tone: 'neutral' };
  const cls = {
    success: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    danger: 'bg-red-100 text-red-800 border-red-200',
    warning: 'bg-amber-100 text-amber-800 border-amber-200',
    neutral: 'bg-stone-100 text-stone-700 border-stone-200',
  }[meta.tone];
  return (
    <Badge variant="outline" className={cls} data-testid={`event-badge-${eventType}`}>
      {meta.label}
    </Badge>
  );
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-NZ', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (_) {
    return iso;
  }
}

export default function EmailDeliveryPage() {
  const [windowDays, setWindowDays] = useState(7);
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [sends, setSends] = useState([]);
  const [eventFilter, setEventFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, eRes, sendsRes] = await Promise.all([
        axios.get(`${API}/admin/email-delivery/summary`, {
          params: { days: windowDays },
          withCredentials: true,
        }),
        axios.get(`${API}/admin/email-delivery/events`, {
          params: {
            days: windowDays,
            ...(eventFilter !== 'all' ? { event_type: eventFilter } : {}),
            limit: 200,
          },
          withCredentials: true,
        }),
        axios.get(`${API}/admin/email-delivery/recent-sends`, {
          params: { days: windowDays, limit: 100 },
          withCredentials: true,
        }),
      ]);
      setSummary(sRes.data);
      setEvents(eRes.data.events || []);
      setSends(sendsRes.data.sends || []);
    } catch (err) {
      toast.error('Failed to load email delivery data');
    } finally {
      setLoading(false);
    }
  }, [windowDays, eventFilter]);

  useEffect(() => { load(); }, [load]);

  const renderSummaryCard = (title, value, icon, tone) => {
    const Icon = icon;
    const toneCls = {
      success: 'text-emerald-700 bg-emerald-50 border-emerald-200',
      danger: 'text-red-700 bg-red-50 border-red-200',
      warning: 'text-amber-700 bg-amber-50 border-amber-200',
      neutral: 'text-stone-700 bg-stone-50 border-stone-200',
    }[tone];
    return (
      <Card className={`border ${toneCls}`} data-testid={`summary-${title.toLowerCase()}`}>
        <CardContent className="pt-6">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium opacity-70">{title}</p>
              <p className="text-3xl font-semibold mt-1">{value}</p>
            </div>
            <Icon className="h-8 w-8 opacity-50" />
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="email-delivery-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-semibold flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Mail className="h-7 w-7" />
            Email Delivery
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Sends, bounces and complaints from the last {windowDays} days. Powered by Resend webhooks.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(windowDays)} onValueChange={(v) => setWindowDays(Number(v))}>
            <SelectTrigger className="w-32" data-testid="window-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Last 24h</SelectItem>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={load} disabled={loading} data-testid="refresh-btn">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {renderSummaryCard('Delivered', summary?.delivered ?? '—', CheckCircle2, 'success')}
        {renderSummaryCard('Bounced', summary?.bounces ?? '—', AlertTriangle, summary?.bounces ? 'danger' : 'neutral')}
        {renderSummaryCard('Complaints', summary?.complaints ?? '—', ShieldAlert, summary?.complaints ? 'danger' : 'neutral')}
        {renderSummaryCard('Total events', summary?.total ?? '—', Inbox, 'neutral')}
      </div>

      {/* Events table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-lg">Recent Events</CardTitle>
          <Select value={eventFilter} onValueChange={setEventFilter}>
            <SelectTrigger className="w-48" data-testid="event-filter">
              <SelectValue placeholder="All events" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All events</SelectItem>
              <SelectItem value="email.delivered">Delivered only</SelectItem>
              <SelectItem value="email.bounced">Bounced only</SelectItem>
              <SelectItem value="email.complained">Complaints only</SelectItem>
              <SelectItem value="email.opened">Opened only</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {events.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center" data-testid="no-events">
              No events in this window. Either nothing was sent yet, or webhooks haven't been hooked up in the Resend dashboard.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-stone-200 text-left">
                  <tr>
                    <th className="py-2 px-2 font-semibold">When</th>
                    <th className="py-2 px-2 font-semibold">Event</th>
                    <th className="py-2 px-2 font-semibold">To</th>
                    <th className="py-2 px-2 font-semibold">Subject</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((ev, i) => (
                    <tr key={`${ev.email_id || 'evt'}-${i}`} className="border-b border-stone-100" data-testid={`event-row-${i}`}>
                      <td className="py-2 px-2 whitespace-nowrap text-muted-foreground">{formatDate(ev.received_at)}</td>
                      <td className="py-2 px-2">{eventBadge(ev.event_type)}</td>
                      <td className="py-2 px-2">{Array.isArray(ev.to) ? ev.to.join(', ') : (ev.to || '')}</td>
                      <td className="py-2 px-2 max-w-md truncate">{ev.subject || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Outbound log */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Recent Sends (Outbound Log)</CardTitle>
          <p className="text-xs text-muted-foreground">
            Every email this system kicked out. Compare against the events table above to confirm delivery.
          </p>
        </CardHeader>
        <CardContent>
          {sends.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center" data-testid="no-sends">
              No sends in this window.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-stone-200 text-left">
                  <tr>
                    <th className="py-2 px-2 font-semibold">When</th>
                    <th className="py-2 px-2 font-semibold">Transport</th>
                    <th className="py-2 px-2 font-semibold">To</th>
                    <th className="py-2 px-2 font-semibold">Subject</th>
                    <th className="py-2 px-2 font-semibold">Mode</th>
                  </tr>
                </thead>
                <tbody>
                  {sends.map((s, i) => (
                    <tr key={`send-${i}`} className="border-b border-stone-100" data-testid={`send-row-${i}`}>
                      <td className="py-2 px-2 whitespace-nowrap text-muted-foreground">{formatDate(s.sent_at)}</td>
                      <td className="py-2 px-2">
                        <Badge variant="outline" className={s.transport === 'resend'
                          ? 'bg-blue-50 text-blue-700 border-blue-200'
                          : 'bg-stone-100 text-stone-700 border-stone-200'}>
                          {s.transport || 'smtp'}
                        </Badge>
                      </td>
                      <td className="py-2 px-2 max-w-xs truncate">
                        {Array.isArray(s.intended_to) ? s.intended_to.join(', ') : (s.intended_to || '')}
                      </td>
                      <td className="py-2 px-2 max-w-md truncate">{s.subject}</td>
                      <td className="py-2 px-2">
                        {s.dev_mode ? (
                          <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">Dev</Badge>
                        ) : (
                          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Live</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
